package com.trevorwiebe.timesheet.settlements.presentation

import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.carrier.domain.CarrierRepository
import com.trevorwiebe.timesheet.admin.ownerOperator.domain.OwnerOperatorRepository
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.centsToDollarInput
import com.trevorwiebe.timesheet.core.domain.dollarsToCents
import com.trevorwiebe.timesheet.core.domain.formatPercent
import com.trevorwiebe.timesheet.core.domain.model.Carrier
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.OwnerOperator
import com.trevorwiebe.timesheet.core.domain.toPercentOrNull
import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import com.trevorwiebe.timesheet.loads.domain.LoadsRepository
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.presentation.admin.todayDate
import com.trevorwiebe.timesheet.mileage.domain.LocationRepository
import com.trevorwiebe.timesheet.mileage.domain.MileageRepository
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.settlements.data.dto.SendSettlementResponse
import com.trevorwiebe.timesheet.settlements.domain.SettlementRepository
import com.trevorwiebe.timesheet.settlements.domain.buildDraftCarrierSettlement
import com.trevorwiebe.timesheet.settlements.domain.buildDraftSettlement
import com.trevorwiebe.timesheet.settlements.domain.refreshedSettlementLines
import com.trevorwiebe.timesheet.settlements.domain.settlementMiles
import com.trevorwiebe.timesheet.settlements.domain.model.Settlement
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementDeduction
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SettlementsViewModel(
    private val settlementRepository: SettlementRepository,
    private val loadsRepository: LoadsRepository,
    private val ownerOperatorRepository: OwnerOperatorRepository,
    private val coreRepository: CoreRepository,
    private val locationRepository: LocationRepository,
    private val mileageRepository: MileageRepository,
    private val carrierRepository: CarrierRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(SettlementsState(today = todayDate()))
    val state = _state.asStateFlow()

    init {
        loadSettlements()
        loadLoads()
        loadOwnerOperators()
        loadCarriers()
        loadUsers()
        loadLocations()
    }

    fun onEvent(event: SettlementsEvent) {
        when (event) {
            is SettlementsEvent.OnSelectTab -> _state.update { it.copy(tab = event.tab) }

            SettlementsEvent.OnNewSettlementClick ->
                _state.update { it.copy(newSettlement = NewSettlementState(endDate = it.today)) }
            SettlementsEvent.OnDismissNewSettlement -> _state.update {
                if (it.newSettlement?.isSaving == true) it else it.copy(newSettlement = null)
            }
            is SettlementsEvent.OnSelectOwnerOperator ->
                updateForm(reselect = true) { it.copy(ownerOperatorId = event.ownerOperatorId, carrierId = "") }
            is SettlementsEvent.OnSelectCarrier ->
                updateForm(reselect = true) { it.copy(carrierId = event.carrierId, ownerOperatorId = "") }
            SettlementsEvent.OnStartDateClick -> updateForm { it.copy(showStartPicker = true) }
            SettlementsEvent.OnEndDateClick -> updateForm { it.copy(showEndPicker = true) }
            is SettlementsEvent.OnStartDateSelected ->
                updateForm(reselect = true) { it.copy(startDate = event.date, showStartPicker = false) }
            is SettlementsEvent.OnEndDateSelected ->
                updateForm(reselect = true) { it.copy(endDate = event.date, showEndPicker = false) }
            SettlementsEvent.OnDismissDatePickers ->
                updateForm { it.copy(showStartPicker = false, showEndPicker = false) }
            is SettlementsEvent.OnToggleLoad -> updateForm { form ->
                val ids = form.selectedLoadIds
                form.copy(selectedLoadIds = if (event.loadId in ids) ids - event.loadId else ids + event.loadId)
            }
            SettlementsEvent.OnSelectAllLoads -> updateForm(reselect = true) { it }
            SettlementsEvent.OnClearLoadSelection -> updateForm { it.copy(selectedLoadIds = emptySet()) }
            SettlementsEvent.OnCreateSettlement -> createSettlement()

            is SettlementsEvent.OnSettlementClick -> {
                _state.update { it.copy(selectedSettlementId = event.settlementId) }
                syncSelectedDraft()
            }
            SettlementsEvent.OnCloseSettlement ->
                _state.update { it.copy(selectedSettlementId = null, showDeleteConfirm = false) }
            SettlementsEvent.OnRequestDelete -> _state.update { it.copy(showDeleteConfirm = true) }
            SettlementsEvent.OnDismissDelete -> _state.update { it.copy(showDeleteConfirm = false) }
            SettlementsEvent.OnConfirmDelete -> deleteSelectedSettlement()
            SettlementsEvent.OnRequestMarkSent -> _state.update { it.copy(showMarkSentConfirm = true) }
            SettlementsEvent.OnDismissMarkSent -> _state.update {
                if (it.isMarkingSent) it else it.copy(showMarkSentConfirm = false)
            }
            SettlementsEvent.OnConfirmMarkSent -> markSelectedSettlementSent()

            is SettlementsEvent.OnLineClick -> _state.update { state ->
                val settlement = state.selectedSettlement ?: return@update state
                val line = settlement.lines.firstOrNull { it.loadId == event.loadId } ?: return@update state
                if (!settlement.isDraft) return@update state
                val charges = line.charges.map { ChargePercentInput(it.charge, it.amount, it.payPercent.formatPercent()) }
                state.copy(lineDialog = LinePayDialogState(line.loadId, line.basePayPercent.formatPercent(), charges))
            }
            is SettlementsEvent.OnLineBasePercentChanged -> _state.update { state ->
                state.copy(lineDialog = state.lineDialog?.copy(baseText = event.text, error = null))
            }
            is SettlementsEvent.OnLinePercentChanged -> _state.update { state ->
                val dialog = state.lineDialog ?: return@update state
                val charges = dialog.charges.map { if (it.charge == event.charge) it.copy(percentText = event.text) else it }
                state.copy(lineDialog = dialog.copy(charges = charges, error = null))
            }
            SettlementsEvent.OnDismissLineDialog -> _state.update {
                if (it.lineDialog?.isSaving == true) it else it.copy(lineDialog = null)
            }
            SettlementsEvent.OnSaveLinePercent -> saveLinePercent()

            SettlementsEvent.OnAddDeductionClick -> _state.update {
                val settlement = it.selectedSettlement
                if (settlement?.isDraft != true || settlement.isCarrier) it else it.copy(deductionDialog = DeductionDialogState())
            }
            is SettlementsEvent.OnDeductionClick -> _state.update { state ->
                val settlement = state.selectedSettlement ?: return@update state
                val deduction = settlement.deductions.getOrNull(event.index) ?: return@update state
                if (!settlement.isDraft) return@update state
                state.copy(
                    deductionDialog = DeductionDialogState(
                        index = event.index,
                        description = deduction.description,
                        amountText = deduction.amount.centsToDollarInput(),
                    )
                )
            }
            is SettlementsEvent.OnDeductionDescriptionChanged -> _state.update {
                it.copy(deductionDialog = it.deductionDialog?.copy(description = event.text, error = null))
            }
            is SettlementsEvent.OnDeductionAmountChanged -> _state.update {
                it.copy(deductionDialog = it.deductionDialog?.copy(amountText = event.text, error = null))
            }
            SettlementsEvent.OnDismissDeduction -> _state.update {
                if (it.deductionDialog?.isSaving == true) it else it.copy(deductionDialog = null)
            }
            SettlementsEvent.OnSaveDeduction -> saveDeduction(delete = false)
            SettlementsEvent.OnDeleteDeduction -> saveDeduction(delete = true)

            SettlementsEvent.OnViewPdfClick -> viewSelectedPdf()
            SettlementsEvent.OnPdfOpened -> _state.update { it.copy(pdfUrlToOpen = null) }
            SettlementsEvent.OnSendClick -> _state.update { state ->
                val settlement = state.selectedSettlement ?: return@update state
                val email = if (settlement.isCarrier) {
                    state.carriers.firstOrNull { it.id == settlement.carrierId }?.email.orEmpty()
                } else {
                    state.ownerOperators.firstOrNull { it.id == settlement.ownerOperatorId }?.email.orEmpty()
                }
                state.copy(sendDialog = SendSettlementState(to = email))
            }
            is SettlementsEvent.OnSendRecipientChanged -> _state.update {
                it.copy(sendDialog = it.sendDialog?.copy(to = event.text, error = null))
            }
            SettlementsEvent.OnDismissSend -> _state.update {
                if (it.sendDialog?.isSending == true) it else it.copy(sendDialog = null)
            }
            SettlementsEvent.OnConfirmSend -> sendSelectedSettlement()

            SettlementsEvent.OnInfoShown -> _state.update { it.copy(infoMessage = null) }
            SettlementsEvent.OnErrorShown -> _state.update { it.copy(error = null) }
        }
    }

    /**
     * Applies [transform] to the open new-settlement form. With [reselect], the checked loads reset
     * to every payable load the form now shows — the right default after the owner operator or range changes.
     */
    private fun updateForm(reselect: Boolean = false, transform: (NewSettlementState) -> NewSettlementState) {
        _state.update { state ->
            val form = state.newSettlement ?: return@update state
            val updated = state.copy(newSettlement = transform(form).copy(error = null))
            if (!reselect) return@update updated
            val payable = updated.settleableLoads.filter { it.isPayable }.mapTo(mutableSetOf()) { it.load.id }
            updated.copy(newSettlement = updated.newSettlement?.copy(selectedLoadIds = payable))
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun createSettlement() {
        val state = _state.value
        if (!state.canCreateSettlement) return
        val form = state.newSettlement ?: return
        val lines = state.settleableLoads
            .filter { it.load.id in form.selectedLoadIds && it.isPayable }
            .map { it.line }
        val carrier = state.newSettlementCarrier
        if (carrier != null) {
            updateForm { it.copy(isSaving = true) }
            viewModelScope.launch { saveNewSettlement(buildDraftCarrierSettlement(carrier, lines, state.today)) }
            return
        }
        val ownerOperator = state.newSettlementOwnerOperator ?: return
        val start = lines.minOf { it.date }
        val end = lines.maxOf { it.date }
        val driverIds = state.newSettlementDrivers.mapTo(mutableSetOf()) { it.id }

        updateForm { it.copy(isSaving = true) }
        viewModelScope.launch {
            val entries = mileageRepository.queryMileageEntries(start, end).first()
            if (!entries.error.isNullOrEmpty()) {
                _state.update { s -> s.copy(newSettlement = s.newSettlement?.copy(isSaving = false, error = entries.error)) }
                return@launch
            }
            val miles = settlementMiles(entries.data as List<MileageEntry>, driverIds, start, end)
            saveNewSettlement(buildDraftSettlement(ownerOperator, lines, miles, state.today))
        }
    }

    private suspend fun saveNewSettlement(draft: Settlement) {
        val result = settlementRepository.createSettlement(draft)
        val saved = result.data as? Settlement
        if (result.error.isNullOrEmpty() && saved != null) {
            _state.update {
                it.copy(newSettlement = null, selectedSettlementId = saved.id, tab = SettlementListTab.DRAFTS)
            }
        } else {
            _state.update { s -> s.copy(newSettlement = s.newSettlement?.copy(isSaving = false, error = result.error)) }
        }
    }

    private var syncingDraftId: String? = null

    /** Keeps an open draft's charges in step with its loads, so edits made after drafting show up. */
    private fun syncSelectedDraft() {
        val state = _state.value
        val settlement = state.selectedSettlement ?: return
        if (!settlement.isDraft || syncingDraftId == settlement.id) return
        val lines = refreshedSettlementLines(settlement, state.loads, state.locations)
        if (lines == settlement.lines) return
        syncingDraftId = settlement.id
        viewModelScope.launch {
            val result = settlementRepository.updateDraftSettlement(settlement.copy(lines = lines))
            syncingDraftId = null
            if (!result.error.isNullOrEmpty()) _state.update { it.copy(error = result.error) }
        }
    }

    private fun saveLinePercent() {
        val state = _state.value
        val dialog = state.lineDialog ?: return
        val settlement = state.selectedSettlement ?: return
        val basePercent = dialog.baseText.toPercentOrNull()
        val percents = dialog.charges.mapNotNull { input -> input.percentText.toPercentOrNull()?.let { input.charge to it } }
        if (basePercent == null || percents.size != dialog.charges.size) {
            _state.update { it.copy(lineDialog = dialog.copy(error = "Enter percentages from 0 to 100.")) }
            return
        }
        val updated = settlement.copy(
            lines = settlement.lines.map { line ->
                if (line.loadId != dialog.loadId) line
                else percents.fold(line.copy(basePayPercent = basePercent)) { acc, (charge, percent) -> acc.withPayPercent(charge, percent) }
            }
        )
        _state.update { it.copy(lineDialog = dialog.copy(isSaving = true)) }
        viewModelScope.launch {
            val result = settlementRepository.updateDraftSettlement(updated)
            _state.update {
                if (result.error.isNullOrEmpty()) it.copy(lineDialog = null)
                else it.copy(lineDialog = it.lineDialog?.copy(isSaving = false, error = result.error))
            }
        }
    }

    private fun saveDeduction(delete: Boolean) {
        val state = _state.value
        val dialog = state.deductionDialog ?: return
        val settlement = state.selectedSettlement ?: return
        val description = dialog.description.trim()
        val amount = dialog.amountText.dollarsToCents()
        val deductions = settlement.deductions.toMutableList()
        if (delete) {
            dialog.index?.let { deductions.removeAt(it) } ?: return
        } else {
            val problem = when {
                description.isEmpty() -> "Enter what the deduction is for."
                amount <= 0 -> "Enter the deduction amount."
                else -> null
            }
            if (problem != null) {
                _state.update { it.copy(deductionDialog = dialog.copy(error = problem)) }
                return
            }
            val deduction = SettlementDeduction(description, amount)
            dialog.index?.let { deductions[it] = deduction } ?: deductions.add(deduction)
        }
        _state.update { it.copy(deductionDialog = dialog.copy(isSaving = true, error = null)) }
        viewModelScope.launch {
            val result = settlementRepository.updateDraftSettlement(settlement.copy(deductions = deductions))
            _state.update {
                if (result.error.isNullOrEmpty()) it.copy(deductionDialog = null)
                else it.copy(deductionDialog = it.deductionDialog?.copy(isSaving = false, error = result.error))
            }
        }
    }

    private fun deleteSelectedSettlement() {
        val settlement = _state.value.selectedSettlement ?: return
        if (!settlement.isDraft) return
        _state.update { it.copy(isDeleting = true) }
        viewModelScope.launch {
            val result = settlementRepository.deleteDraftSettlement(settlement)
            _state.update {
                if (result.error.isNullOrEmpty()) {
                    it.copy(isDeleting = false, showDeleteConfirm = false, selectedSettlementId = null)
                } else {
                    it.copy(isDeleting = false, showDeleteConfirm = false, error = result.error)
                }
            }
        }
    }

    private fun markSelectedSettlementSent() {
        val state = _state.value
        val settlement = state.selectedSettlement ?: return
        if (!settlement.isDraft) return
        _state.update { it.copy(isMarkingSent = true) }
        viewModelScope.launch {
            val result = settlementRepository.markSettlementSent(settlement, state.today, sentTo = "")
            _state.update {
                it.copy(
                    isMarkingSent = false,
                    showMarkSentConfirm = false,
                    error = result.error?.takeIf { e -> e.isNotEmpty() },
                )
            }
        }
    }

    /**
     * Opens the selected settlement's PDF. A draft's PDF is rebuilt first so it reflects its latest
     * deductions; a sent settlement's PDF is the record and is shown as stored.
     */
    private fun viewSelectedPdf() {
        val settlement = _state.value.selectedSettlement ?: return
        if (_state.value.isGeneratingPdf) return
        _state.update { it.copy(isGeneratingPdf = true) }
        viewModelScope.launch {
            var path = settlement.pdfPath
            if (settlement.isDraft || path.isBlank()) {
                val generated = settlementRepository.generateSettlementPdf(settlement.id)
                if (!generated.error.isNullOrEmpty()) {
                    _state.update { it.copy(isGeneratingPdf = false, error = generated.error) }
                    return@launch
                }
                path = generated.data as String
            }
            val url = settlementRepository.getPdfUrl(path)
            _state.update {
                if (url.error.isNullOrEmpty()) it.copy(isGeneratingPdf = false, pdfUrlToOpen = url.data as String)
                else it.copy(isGeneratingPdf = false, error = url.error)
            }
        }
    }

    private fun sendSelectedSettlement() {
        val state = _state.value
        val settlement = state.selectedSettlement ?: return
        val dialog = state.sendDialog ?: return
        val to = dialog.to.trim()
        if (!EMAIL_PATTERN.matches(to)) {
            _state.update { it.copy(sendDialog = dialog.copy(error = "Enter a valid email address.")) }
            return
        }
        _state.update { it.copy(sendDialog = dialog.copy(isSending = true, error = null)) }
        viewModelScope.launch {
            val result = settlementRepository.sendSettlement(settlement.id, to)
            val response = result.data as? SendSettlementResponse
            if (result.error.isNullOrEmpty() && response != null) {
                _state.update {
                    it.copy(
                        sendDialog = null,
                        infoTitle = if (response.sent) "Settlement sent" else "Marked sent, not emailed",
                        infoMessage = if (response.sent) {
                            "Settlement ${settlement.numberLabel} was emailed to $to."
                        } else {
                            "Settlement ${settlement.numberLabel} was marked sent, but no email went out (test environment)."
                        },
                    )
                }
            } else {
                _state.update { s -> s.copy(sendDialog = s.sendDialog?.copy(isSending = false, error = result.error)) }
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadSettlements() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            settlementRepository.getSettlements().collect { result ->
                _state.update {
                    if (result.error.isNullOrEmpty()) it.copy(settlements = result.data as List<Settlement>, isLoading = false)
                    else it.copy(isLoading = false, error = result.error)
                }
            }
        })
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadLoads() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            loadsRepository.queryLoads().collect { result ->
                val loads = result.data as? List<Load> ?: return@collect
                _state.update { it.copy(loads = loads) }
                syncSelectedDraft()
            }
        })
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadOwnerOperators() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            ownerOperatorRepository.getOwnerOperators().collect { result ->
                val ownerOperators = result.data as? List<OwnerOperator> ?: return@collect
                _state.update { it.copy(ownerOperators = ownerOperators) }
            }
        })
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadCarriers() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            carrierRepository.getCarriers().collect { result ->
                val carriers = result.data as? List<Carrier> ?: return@collect
                _state.update { it.copy(carriers = carriers) }
            }
        })
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadUsers() {
        viewModelScope.launch {
            val result = coreRepository.getUserList()
            val users = result.data as? List<DatabaseUserModel> ?: return@launch
            _state.update { it.copy(users = users) }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadLocations() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            locationRepository.getLocations().collect { result ->
                val locations = result.data as? List<Location> ?: return@collect
                _state.update { it.copy(locations = locations) }
                syncSelectedDraft()
            }
        })
    }
}

private val EMAIL_PATTERN = Regex("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")
